/**
 * @fileoverview Rehearsals page ("Studio Quiet") — record practice sessions in a
 * full-screen "cockpit" overlay (concentric breathing rings + mono timer) and
 * browse past recordings with AI + instructor feedback. The recorder is reached
 * from the sidebar/hero (?record=1), from an assigned lesson (?lesson=<id>), or
 * the in-page Record button.
 *
 * Below the rehearsal library sits the manual practice log — the web twin of
 * the mobile StudentPracticeScreen's "Log time" half: "Log practice" (the
 * portal's one dialog, StudentLogPracticeDialog, since C6), a Sessions / Total
 * Hours / Avg Min strip and the history list. Recording and logging are SEPARATE
 * runtime kill switches (rehearsalsEnabled / practiceLogsEnabled), so each
 * half renders on its own and the page only says "off" when both are.
 */

import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { StudentAuthContext } from "@/App";
import studentApi, { listPracticeLogs, deletePracticeLog } from "@/lib/studentApi";
import { getErrorMessage } from "@/lib/api";
import { practiceLogsEnabled, rehearsalsEnabled } from "@/lib/featureFlags";
import { pickAudioMimeType, isIOSBrowser } from "@/lib/recording";
import { cn } from "@/lib/utils";
import { Card, Icon, Button } from "@/components/studio/primitives";
import { SHeader, SEmpty } from "@/components/studio/studentUi";
import UnreviewedAiNotice from "@/components/UnreviewedAiNotice";
import { useStudentPractice } from "@/components/StudentPracticeContext";

const MAX_RECORDING_SECONDS = 15 * 60;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

/**
 * A practice log's `date` is a bare YYYY-MM-DD; `new Date("2026-09-16")` parses
 * that as UTC midnight, which renders as the day BEFORE in any western
 * timezone. Build it from its parts so the day the student picked is the day
 * shown. Timestamps (recordings' `created_at`) go through Date as usual.
 */
const parseDate = (dateStr) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dateStr);
};

const formatDate = (dateStr) =>
  parseDate(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

/** True when a released recording carries any Hans-written feedback. */
const hasGeneratedFeedback = (rec) =>
  !!(rec.ai_notes || rec.ai_strengths?.length || rec.ai_improvements?.length);

/** Processing/feedback status pill for a recording. */
const StatusBadge = ({ status, released, aiSkipped }) => {
  const base = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-sans text-[11px] font-semibold";
  if (status === "pending" || status === "processing") {
    return (
      <span className={cn(base, "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]")}>
        <Icon name="loader" size={12} className="animate-spin" /> Processing
      </span>
    );
  }
  if (status === "failed") {
    return <span className={cn(base, "border-[var(--state-danger-border)] bg-[var(--state-danger-dim)] text-[var(--state-danger)]")}>Failed</span>;
  }
  if (status === "complete" && !released) {
    // ai_skipped = the AI pass was skipped (teacher's plan) — the teacher
    // reviews personally, so don't imply automatic feedback is on its way.
    if (aiSkipped) {
      return (
        <span className={cn(base, "border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-tertiary)]")} data-testid="badge-with-teacher">
          <Icon name="user" size={12} /> With your teacher
        </span>
      );
    }
    return (
      <span className={cn(base, "border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-tertiary)]")}>
        <Icon name="lock" size={12} /> Awaiting feedback
      </span>
    );
  }
  if (status === "complete" && released) {
    return (
      <span className={cn(base, "border-[var(--state-success-border)] bg-[var(--state-success-dim)] text-[var(--state-success)]")}>
        <Icon name="check" size={12} /> Feedback ready
      </span>
    );
  }
  return null;
};

/** One rehearsals summary stat (mono value + caption). */
const RehearsalStat = ({ value, label }) => (
  // Padding via `style` (not `p-*` classes): Card sets an inline `padding`, which
  // wins over utility classes — passing them here would silently zero the padding.
  <Card style={{ padding: "22px 26px" }}>
    <div className="font-mono text-[40px] font-light leading-none tracking-[-0.02em] text-[var(--text-primary)]">{value}</div>
    <div className="mt-2.5 font-sans text-[13px] text-[var(--text-tertiary)]">{label}</div>
  </Card>
);

/** One practice-log stat (mono value + caption) — same tile as RehearsalStat. */
const LogStat = ({ value, label, testId }) => (
  <Card style={{ padding: "22px 26px" }}>
    <div className="font-mono text-[40px] font-light leading-none tracking-[-0.02em] text-[var(--text-primary)]" data-testid={testId}>{value}</div>
    <div className="mt-2.5 font-sans text-[13px] text-[var(--text-tertiary)]">{label}</div>
  </Card>
);

/**
 * Manual practice log: the web port of the mobile screen's "Log time" mode.
 * Only mounted when practiceLogsEnabled(student) — the endpoints 404 when the
 * kill switch is off, so an unmounted section also never asks for logs it
 * cannot have.
 */
function PracticeLogSection() {
  const { openLog, version } = useStudentPractice();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchLogs = useCallback(async (signal) => {
    setLoadError(false);
    try {
      const data = await listPracticeLogs(signal ? { signal } : {});
      setLogs(data);
    } catch (err) {
      if (signal?.aborted) return;
      console.error("Failed to load practice logs:", err);
      setLoadError(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  // Read on show and again after every log made anywhere in the portal.
  useEffect(() => {
    const controller = new AbortController();
    fetchLogs(controller.signal);
    return () => controller.abort();
  }, [fetchLogs, version]);

  const totalMinutes = logs.reduce((sum, l) => sum + (l.duration_minutes || 0), 0);
  const totalHours = (totalMinutes / 60).toFixed(1);
  const avgMinutes = logs.length > 0 ? Math.round(totalMinutes / logs.length) : 0;

  const handleDelete = async (logId) => {
    if (!window.confirm("Remove this practice session?")) return;
    try {
      await deletePracticeLog(logId);
      setLogs((prev) => prev.filter((l) => (l.log_id ?? l.id) !== logId));
    } catch (_err) {
      toast.error("Failed to delete log.");
    }
  };

  return (
    <section className="mt-12" data-testid="practice-log-section">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Practice log</h2>
        <Button variant="ghost" icon="plus" onPress={() => openLog()} data-testid="practice-log-open">
          Log practice
        </Button>
      </div>

      {/* Stats strip — Sessions / Total Hours / Avg Min, as on mobile. */}
      <div className="mb-5 grid grid-cols-3 gap-5">
        <LogStat value={logs.length} label="Sessions" testId="practice-log-sessions" />
        <LogStat value={totalHours} label="Total Hours" testId="practice-log-hours" />
        <LogStat value={avgMinutes} label="Avg Min" testId="practice-log-avg" />
      </div>

      {loading ? (
        <div className="space-y-2.5">
          {[1, 2].map((i) => <div key={i} className="skeleton h-[68px] rounded-[20px]" />)}
        </div>
      ) : loadError ? (
        <Card className="flex flex-wrap items-center justify-between gap-3" data-testid="practice-log-error">
          <div className="flex items-center gap-2.5 font-sans text-[15px] text-[var(--text-secondary)]">
            <Icon name="alert" size={18} strokeWidth={1.5} className="text-[var(--state-danger)]" />
            Couldn&apos;t load your practice log.
          </div>
          <Button variant="bare" icon="refresh" onPress={() => fetchLogs()}>Retry</Button>
        </Card>
      ) : logs.length === 0 ? (
        <Card padding={0}>
          <SEmpty
            icon="clock"
            title="No practice logs yet"
            body="Log your first session to start keeping track of your practice time."
            cta="Log practice"
            ctaIcon="plus"
            onCta={() => openLog()}
          />
        </Card>
      ) : (
        <div className="space-y-2.5" data-testid="practice-log-list">
          {logs.map((log, index) => {
            const id = log.log_id ?? log.id ?? String(index);
            return (
              <Card key={id} className="flex items-center justify-between gap-3" style={{ padding: 14 }} data-testid="practice-log-row">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]">
                    <Icon name="clock" size={18} strokeWidth={1.5} className="text-[var(--accent-yellow-text)]" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{log.duration_minutes} min</div>
                    <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{formatDate(log.date)}</div>
                    {log.practice_item && (
                      <p className="mt-1 font-sans text-[13px] leading-snug text-[var(--text-secondary)]">For {log.practice_item}</p>
                    )}
                    {log.notes && (
                      <p className="mt-1 line-clamp-2 whitespace-pre-wrap font-sans text-[13px] leading-snug text-[var(--text-quaternary)]">{log.notes}</p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(id)}
                  aria-label="Delete log"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--state-danger)]"
                >
                  <Icon name="trash" size={16} strokeWidth={1.5} />
                </button>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}

const StudentPracticePage = () => {
  const { student } = useContext(StudentAuthContext) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  // Recording and manual logging are SEPARATE kill switches (see the
  // fileoverview), so each half is decided on its own.
  const showRehearsals = rehearsalsEnabled(student);
  const showLogs = practiceLogsEnabled(student);

  // Recording overlay state
  const [showRecorder, setShowRecorder] = useState(false);
  const [recStage, setRecStage] = useState("select"); // select | ready | recording | uploading | success
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [lessonId, setLessonId] = useState(null);
  const [lessonTitle, setLessonTitle] = useState("");
  const [recentLessons, setRecentLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);

  // Library state
  const [recordings, setRecordings] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [instruments, setInstruments] = useState([]);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mimeTypeRef = useRef("audio/webm"); // negotiated per capture (Safari records MP4, not WebM)
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const recordingActiveRef = useRef(false); // true from start until OUR stop — distinguishes an external capture death

  const fetchRecordings = useCallback(async (signal) => {
    try {
      const res = await studentApi.get("/student/practice/recordings", signal ? { signal } : {});
      setRecordings(res.data || []);
    } catch (_err) {
      if (signal?.aborted) return;
      console.error("Failed to load recordings:", _err);
    } finally {
      if (!signal?.aborted) setLoadingHistory(false);
    }
  }, []);

  const fetchRecentLessons = useCallback(async () => {
    setLoadingLessons(true);
    try {
      const res = await studentApi.get("/student/lessons", { params: { limit: 10 } });
      setRecentLessons(res.data || []);
    } catch (_err) {
      setRecentLessons([]);
    } finally {
      setLoadingLessons(false);
    }
  }, []);

  const openRecorder = useCallback(() => {
    setShowRecorder(true);
    setRecStage("select");
    setAudioBlob(null);
    setDuration(0);
    setSelectedInstrument("");
    setLessonId(null);
    setLessonTitle("");
    fetchRecentLessons();
  }, [fetchRecentLessons]);

  // Mount: load instruments + recordings. Skipped when rehearsals are off —
  // the recording endpoints 404 behind the kill switch, so don't ask.
  useEffect(() => {
    if (!showRehearsals) {
      setLoadingHistory(false);
      return undefined;
    }
    const controller = new AbortController();
    studentApi.get("/student/instruments", { signal: controller.signal })
      .then((res) => setInstruments(res.data || []))
      .catch((err) => { if (!controller.signal.aborted) console.error(err); });
    fetchRecordings(controller.signal);
    return () => controller.abort();
  }, [fetchRecordings, showRehearsals]);

  // React to ?record=1 / ?lesson=<id> query params (from sidebar, hero, lessons).
  useEffect(() => {
    const lessonParam = searchParams.get("lesson");
    const recordParam = searchParams.get("record");
    if (!lessonParam && !recordParam) return;

    if (lessonParam) {
      setLessonId(lessonParam);
      setLessonTitle("");
      setAudioBlob(null);
      setDuration(0);
      setSelectedInstrument("");
      setShowRecorder(true);
      setRecStage("ready");
      studentApi.get(`/student/lessons/${lessonParam}`)
        .then((res) => setLessonTitle(res.data.title || ""))
        .catch(() => {});
    } else if (recordParam) {
      openRecorder();
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectLesson = (lesson) => {
    setLessonId(lesson.lesson_id);
    setLessonTitle(lesson.title);
    setRecStage("ready");
  };

  const selectOpenSession = () => {
    setLessonId(null);
    setLessonTitle("Open Session");
    setRecStage("ready");
  };

  const stopRecordingCleanup = () => {
    recordingActiveRef.current = false;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setIsRecording(false);
    setIsPaused(false);
  };

  const closeRecorder = () => {
    if (isRecording) stopRecordingCleanup();
    setShowRecorder(false);
    setRecStage("select");
    setAudioBlob(null);
    setDuration(0);
    setLessonId(null);
    setLessonTitle("");
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Negotiate the capture format: Safari (every iPhone browser is
      // WebKit) can't record WebM and throws NotSupportedError if it's
      // hardcoded — it records MP4/AAC instead.
      const mimeType = pickAudioMimeType();
      mimeTypeRef.current = mimeType || "audio/webm";
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = () => {
        setAudioBlob(new Blob(audioChunksRef.current, { type: mimeTypeRef.current }));
      };
      mediaRecorder.onerror = () => handleCaptureFailureRef.current?.();

      // iOS ends the mic track when the system takes the microphone away
      // (screen lock, phone call, app switch). Stop honestly and keep the
      // partial take instead of showing a live recording that isn't.
      const track = stream.getAudioTracks?.()[0] || stream.getTracks?.()[0] || null;
      track?.addEventListener?.("ended", () => handleCaptureFailureRef.current?.());

      recordingActiveRef.current = true;
      mediaRecorder.start(1000);
      setIsRecording(true);
      setDuration(0);
      setAudioBlob(null);
      setRecStage("recording");
      timerRef.current = setInterval(() => setDuration((prev) => prev + 1), 1000);
    } catch (_err) {
      toast.error("Could not access microphone. Please allow microphone access.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setDuration((prev) => prev + 1), 1000);
      setIsPaused(false);
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      recordingActiveRef.current = false;
      // The recorder stops itself when its track dies (screen lock, phone
      // call); calling stop() again would throw InvalidStateError.
      if (mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      setIsPaused(false);
    }
  }, []);
  stopRecordingRef.current = stopRecording;

  /**
   * The capture died out from under us — iOS took the microphone away
   * (screen lock, phone call, app switch) or the recorder errored. Stop
   * honestly, keep the partial take in the preview, and say what happened.
   */
  const handleCaptureFailureRef = useRef(null);
  handleCaptureFailureRef.current = () => {
    if (!recordingActiveRef.current) return; // our own stop, or already handled
    stopRecording();
    toast.error(
      "Recording stopped — your device paused the microphone (screen lock, phone call, or app switch). The audio captured so far is preserved."
    );
  };

  useEffect(() => {
    if (isRecording && !isPaused && duration >= MAX_RECORDING_SECONDS) {
      stopRecordingRef.current?.();
      toast.info("Recording stopped — 15-minute limit reached.");
    }
  }, [duration, isRecording, isPaused]);

  const discardRecording = () => {
    setAudioBlob(null);
    setDuration(0);
    setRecStage("select");
    setLessonId(null);
    setLessonTitle("");
    fetchRecentLessons();
  };

  const uploadRecording = async () => {
    if (!audioBlob) return;
    setRecStage("uploading");
    setUploadProgress(0);
    try {
      // The presigned URL signs the Content-Type, so the header on the S3
      // PUT must match what start-upload was told — the capture's real
      // format, not an assumed one.
      const contentType = audioBlob.type || "audio/webm";
      const { data: startData } = await studentApi.post("/student/practice/start-upload", {
        duration_seconds: duration,
        instrument: selectedInstrument || undefined,
        lesson_id: lessonId || undefined,
        content_type: contentType,
      });
      const { recording_id, upload_url } = startData;

      // Bare axios, never studentApi: the presigned S3 URL must not receive the
      // portal's baseURL or credentials. It also has to be XHR rather than
      // fetch — fetch cannot report request-body progress at all, so the bar
      // used to be three hardcoded jumps (20 → 80 → 100) that sat frozen at
      // 20% for the entire transfer, which on a phone is where the whole wait
      // actually happens.
      const uploadResponse = await axios.put(upload_url, audioBlob, {
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      // Guard a non-2xx that didn't throw, so a rehearsal is never confirmed
      // without the audio having landed in S3.
      if (uploadResponse.status < 200 || uploadResponse.status >= 300) {
        throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
      }
      // The last progress event can land just short of 100 (or not at all if
      // the body was too small to be chunked) — the bytes are gone either way.
      setUploadProgress(100);

      await studentApi.post(`/student/practice/${recording_id}/confirm-upload`);

      setRecStage("success");
      toast.success("Rehearsal submitted!");
      setTimeout(() => {
        setShowRecorder(false);
        setRecStage("select");
        setAudioBlob(null);
        setDuration(0);
        setSelectedInstrument("");
        setLessonId(null);
        setLessonTitle("");
        fetchRecordings();
      }, 2000);
    } catch (err) {
      toast.error(getErrorMessage(err, err.message || "Failed to upload"));
      setRecStage("recording");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recordingActiveRef.current = false; // silence any late 'ended' event
      clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const toggleExpand = (recordingId) => setExpandedId(expandedId === recordingId ? null : recordingId);

  // Stats
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const weekMinutes = Math.round(
    recordings.filter((r) => new Date(r.created_at).getTime() >= weekAgo)
      .reduce((sum, r) => sum + (r.duration_seconds || 0), 0) / 60,
  );
  const feedbackReady = recordings.filter((r) => r.released).length;
  const weekLabel = weekMinutes < 60 ? `${weekMinutes}m` : `${Math.floor(weekMinutes / 60)}h ${weekMinutes % 60}m`;

  // Both halves are runtime kill switches. The sidebar/nav already hide the
  // entry points when they're off, but a bookmark or deep link can still land
  // here — so the page explains the state instead of rendering a recorder or a
  // log form whose endpoints would 404. With both off there is nothing to show
  // at all, and an empty log list would read as "you have never practised",
  // which is a different and much worse message than "this is turned off".
  if (!showRehearsals && !showLogs) {
    return (
      <div className="mx-auto max-w-[1120px] px-6 pb-32 pt-11 md:px-12" data-testid="student-practice-disabled">
        <SHeader eyebrow="Practice" title="Rehearsals" subtitle="Record, submit, and review your practice sessions." />
        <Card padding={0}>
          <SEmpty
            icon="clock"
            title="Practice tracking is off"
            // Deliberately does not say WHO turned it off: this is a platform
            // switch, not the teacher's, and blaming them would be wrong.
            body="Nothing you have already logged or recorded has been deleted."
          />
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-[1120px] px-6 pb-32 pt-11 md:px-12" data-testid="student-rehearsals">
        <SHeader
          eyebrow="Practice"
          title="Rehearsals"
          subtitle="Record, submit, and review your practice sessions."
          action={showRehearsals ? <Button icon="mic" onPress={openRecorder} data-testid="rehearsals-record">Record</Button> : undefined}
        />

        {!showRehearsals ? (
          <Card padding={0} data-testid="student-rehearsals-disabled">
            <SEmpty
              icon="mic"
              title="Rehearsals are turned off"
              body="Recording practice rehearsals isn't available right now. Your lessons and other portal features are unaffected."
            />
          </Card>
        ) : (
        <>
        {/* Stats */}
        <div className="mb-7 grid grid-cols-3 gap-5">
          <RehearsalStat value={recordings.length} label="Total" />
          <RehearsalStat value={weekLabel} label="This week" />
          <RehearsalStat value={feedbackReady} label="With feedback" />
        </div>

        {/* Library */}
        {loadingHistory ? (
          <div className="space-y-2.5">
            {[1, 2, 3].map((i) => <div key={i} className="skeleton h-[76px] rounded-[20px]" />)}
          </div>
        ) : recordings.length === 0 ? (
          <Card padding={0}>
            <SEmpty
              icon="mic"
              title="No rehearsals yet"
              body="Record your practice and review feedback before your next lesson."
              cta="Start your first rehearsal"
              onCta={openRecorder}
            />
          </Card>
        ) : (
          <div className="space-y-2.5">
            {recordings.map((rec) => {
              const expandable = rec.released;
              const expanded = expandedId === rec.recording_id && rec.released;
              return (
                <Card key={rec.recording_id} padding={0} className="overflow-hidden">
                  <button
                    type="button"
                    onClick={() => expandable && toggleExpand(rec.recording_id)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 p-[18px] text-left",
                      expandable ? "cursor-pointer hover:bg-[var(--studio-overlay)]" : "cursor-default",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <span className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{rec.title}</span>
                        {rec.instrument && (
                          <span className="rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-2 py-0.5 font-sans text-[11px] font-medium capitalize text-[var(--accent-yellow-text)]">
                            {rec.instrument}
                          </span>
                        )}
                        <StatusBadge status={rec.processing_status} released={rec.released} aiSkipped={rec.ai_skipped} />
                      </div>
                      <div className="mt-1 flex items-center gap-3 font-sans text-[13px] text-[var(--text-tertiary)]">
                        <span>{formatDate(rec.created_at)}</span>
                        <span className="inline-flex items-center gap-1">
                          <Icon name="clock" size={12} /> {formatDuration(rec.duration_seconds)}
                        </span>
                      </div>
                      {rec.processing_status === "complete" && !rec.released && rec.ai_skipped && (
                        <p className="mt-1 font-sans text-[12px] text-[var(--text-quaternary)]" data-testid="ai-skipped-note">
                          Your teacher reviews recordings personally and will share feedback here.
                        </p>
                      )}
                    </div>
                    {expandable && (
                      <Icon name={expanded ? "chevronUp" : "chevronDown"} size={18} className="shrink-0 text-[var(--text-tertiary)]" />
                    )}
                  </button>

                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="border-t border-[var(--studio-divider)] p-[18px]"
                    >
                      {/* Who wrote the generated part. Unreviewed feedback
                          carries the shared F7 sentence; otherwise a plain
                          authorship line, so "Performance notes" never reads
                          as the teacher's own words. */}
                      {rec.ai_unreviewed ? (
                        <UnreviewedAiNotice className="mb-3" />
                      ) : hasGeneratedFeedback(rec) ? (
                        <p className="mb-3 font-sans text-[12px] italic text-[var(--text-tertiary)]" data-testid="feedback-drafted-by-hans-note">
                          Feedback drafted by Hans
                        </p>
                      ) : null}

                      {rec.ai_notes && (
                        <div className="mb-4">
                          <h4 className="mb-2 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Performance notes</h4>
                          <p className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]">{rec.ai_notes}</p>
                        </div>
                      )}
                      {rec.ai_strengths?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="mb-2 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--state-success)]">Strengths</h4>
                          <ul className="space-y-1">
                            {rec.ai_strengths.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 font-sans text-[15px] text-[var(--text-secondary)]">
                                <Icon name="check" size={14} className="mt-1 shrink-0 text-[var(--state-success)]" />{s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {rec.ai_improvements?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="mb-2 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]">Areas to improve</h4>
                          <ul className="space-y-1">
                            {rec.ai_improvements.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 font-sans text-[15px] text-[var(--text-secondary)]">
                                <span className="mt-0.5 shrink-0 text-[var(--text-tertiary)]">•</span>{s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {rec.instructor_notes && (
                        <div className="mt-4 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-base)] p-3">
                          <h4 className="mb-2 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--accent-yellow-text)]">Instructor notes</h4>
                          <p className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[var(--text-secondary)]">{rec.instructor_notes}</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
        </>
        )}

        {/* Manual practice log — the mobile screen's "Log time" half. */}
        {showLogs && <PracticeLogSection />}
      </div>

      {/* Cockpit overlay */}
      <AnimatePresence>
        {showRecorder && showRehearsals && (
          <RehearsalCockpit
            recStage={recStage}
            isRecording={isRecording}
            isPaused={isPaused}
            duration={duration}
            audioBlob={audioBlob}
            uploadProgress={uploadProgress}
            instruments={instruments}
            selectedInstrument={selectedInstrument}
            setSelectedInstrument={setSelectedInstrument}
            lessonId={lessonId}
            lessonTitle={lessonTitle}
            recentLessons={recentLessons}
            recordings={recordings}
            loadingLessons={loadingLessons}
            onSelectLesson={selectLesson}
            onSelectOpenSession={selectOpenSession}
            onStart={startRecording}
            onPause={pauseRecording}
            onResume={resumeRecording}
            onStop={stopRecording}
            onDiscard={discardRecording}
            onSubmit={uploadRecording}
            onClose={closeRecorder}
          />
        )}
      </AnimatePresence>
    </>
  );
};

/* ─── Cockpit overlay ─── */

const STATUS_LABELS = {
  select: "Choose a lesson",
  ready: "Ready to record",
  uploading: "Submitting",
  success: "Submitted",
};

function RehearsalCockpit(props) {
  const {
    recStage, isRecording, isPaused, duration, audioBlob, uploadProgress,
    instruments, selectedInstrument, setSelectedInstrument, lessonId, lessonTitle,
    recentLessons, recordings, loadingLessons,
    onSelectLesson, onSelectOpenSession, onStart, onPause, onResume, onStop,
    onDiscard, onSubmit, onClose,
  } = props;

  const live = recStage === "recording" && isRecording && !isPaused;
  const showRings = recStage === "ready" || recStage === "recording";
  const canClose = recStage !== "uploading" && recStage !== "success";

  let statusText = STATUS_LABELS[recStage] || "";
  if (recStage === "recording") {
    statusText = audioBlob ? "Review rehearsal" : isPaused ? "Paused" : "Recording rehearsal";
  }

  const audioUrl = audioBlob ? URL.createObjectURL(audioBlob) : null;
  const assignableLessons = recentLessons.filter(
    (l) => l.rehearsal_assigned && !recordings.some((r) => r.lesson_id === l.lesson_id),
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="studio fixed inset-0 z-[60] flex flex-col bg-[var(--studio-base)] text-[var(--text-primary)] [animation:hansSheetUp_.3s_ease]"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-7 md:px-10">
        <div className="flex items-center gap-2.5">
          {live && <span className="h-[9px] w-[9px] rounded-full bg-[var(--accent-yellow)] [animation:hansRecDot_1.2s_infinite]" />}
          <span className={cn(
            "font-sans text-[11px] font-semibold uppercase tracking-[0.12em]",
            live ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-tertiary)]",
          )}>
            {statusText}
          </span>
        </div>
        {canClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close recorder"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)]"
          >
            <Icon name="x" size={20} />
          </button>
        )}
      </div>

      {/* Center */}
      <div className="flex flex-1 flex-col items-center justify-center gap-2 overflow-y-auto px-4 pb-6">
        {/* Select a lesson */}
        {recStage === "select" && (
          <div className="w-full max-w-md text-center">
            <h2 className="font-serif text-[28px] font-normal tracking-[-0.01em] text-[var(--text-primary)]">What are you rehearsing?</h2>
            <p className="mt-2 font-sans text-[15px] text-[var(--text-tertiary)]">Select a lesson to pair with your rehearsal</p>

            {loadingLessons ? (
              <div className="py-10"><Icon name="loader" size={26} className="mx-auto animate-spin text-[var(--accent-yellow-text)]" /></div>
            ) : (
              <div className="mx-auto mt-6 max-h-[46vh] space-y-2 overflow-y-auto">
                {assignableLessons.map((lesson) => (
                  <button
                    key={lesson.lesson_id}
                    type="button"
                    onClick={() => onSelectLesson(lesson)}
                    className="group flex w-full items-center gap-3 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3 text-left transition-colors hover:border-[var(--accent-yellow-border)] hover:bg-[var(--studio-overlay)]"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--studio-interactive)] group-hover:text-[var(--accent-yellow-text)]">
                      <Icon name="fileText" size={16} className="text-[var(--text-secondary)] group-hover:text-[var(--accent-yellow-text)]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-sans text-sm font-medium text-[var(--text-primary)]">{lesson.title}</p>
                      <p className="font-sans text-[12px] text-[var(--text-tertiary)]">
                        {new Date(lesson.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        {lesson.instrument && ` · ${lesson.instrument}`}
                      </p>
                    </div>
                  </button>
                ))}
                {assignableLessons.length === 0 && (
                  <p className="py-3 font-sans text-sm text-[var(--text-tertiary)]">No lessons with rehearsals assigned</p>
                )}

                <div className="flex items-center gap-3 py-2">
                  <div className="h-px flex-1 bg-[var(--studio-divider)]" />
                  <span className="font-sans text-[12px] text-[var(--text-quaternary)]">or</span>
                  <div className="h-px flex-1 bg-[var(--studio-divider)]" />
                </div>

                {recentLessons.some((l) => l.kind === "student") ? (
                  <button
                    type="button"
                    onClick={onSelectOpenSession}
                    className="group flex w-full items-center gap-3 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3 text-left transition-colors hover:bg-[var(--studio-overlay)]"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--studio-interactive)]">
                      <Icon name="headphones" size={16} className="text-[var(--text-secondary)]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-sans text-sm font-medium text-[var(--text-primary)]">Open session</p>
                      <p className="font-sans text-[12px] text-[var(--text-tertiary)]">Practice on your own, no lesson paired</p>
                    </div>
                  </button>
                ) : (
                  <div className="rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3 text-center">
                    <p className="font-sans text-sm text-[var(--text-secondary)]">No lessons to rehearse yet</p>
                    <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                      Once you&apos;ve completed a lesson with your instructor, you can record a rehearsal here.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Ready / Recording — rings + timer + context */}
        {showRings && (
          <>
            <div className="relative flex h-[300px] w-[300px] items-center justify-center">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    width: 120 + i * 70,
                    height: 120 + i * 70,
                    border: `1px solid rgba(244,207,8,${live ? 0.4 - i * 0.1 : 0.15 - i * 0.04})`, // brand-lint-ignore: decorative ring; the alpha is computed per ring and Hans yellow is identical in both themes
                    animation: live ? `hansBreathe ${3 + i}s ease-in-out ${i * 0.3}s infinite` : "none",
                  }}
                />
              ))}
              <div
                className={cn(
                  "flex h-24 w-24 items-center justify-center rounded-full border border-[var(--studio-divider)]",
                  live ? "bg-[var(--accent-yellow)] shadow-[0_0_40px_var(--accent-yellow-glow)]" : "bg-[var(--studio-raised)]",
                )}
              >
                <Icon name="mic" size={34} strokeWidth={2} className={live ? "text-[var(--text-inverse)]" : "text-[var(--text-secondary)]"} />
              </div>
            </div>

            <div className="mt-4 font-mono text-[64px] font-extralight leading-none tracking-[-0.04em] text-[var(--text-primary)]">
              {formatTime(duration)}
            </div>

            {/* Context chip */}
            <div className="mt-5 flex items-center gap-2.5 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-[18px] py-2">
              <Icon name="music" size={16} className="text-[var(--accent-yellow-text)]" />
              <span className={cn("font-sans text-[15px]", lessonId ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-primary)]")}>
                {lessonTitle || "Open session"}
              </span>
              {selectedInstrument && (
                <>
                  <span className="text-[var(--text-quaternary)]">·</span>
                  <span className="font-sans text-[13px] capitalize text-[var(--text-tertiary)]">{selectedInstrument}</span>
                </>
              )}
            </div>

            {/* Instrument picker (ready only, multi-instrument students) */}
            {recStage === "ready" && instruments.length > 1 && (
              <div className="mt-5 flex max-w-md flex-wrap items-center justify-center gap-2">
                {instruments.map((inst) => {
                  const sel = selectedInstrument === inst;
                  return (
                    <button
                      key={inst}
                      type="button"
                      onClick={() => setSelectedInstrument(sel ? "" : inst)}
                      className={cn(
                        "rounded-full border px-4 py-2 font-sans text-[13px] font-medium capitalize transition-colors",
                        sel
                          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                          : "border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                      )}
                    >
                      {inst}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Audio preview after stopping */}
            {audioBlob && (
              <div className="mt-5 w-full max-w-sm rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3">
                <p className="mb-2 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Preview</p>
                <audio controls src={audioUrl} className="h-10 w-full" />
              </div>
            )}

            {/* iOS pauses/kills the mic on screen lock or app switch — a web
                page cannot record in the background there. Warn upfront
                instead of letting a truncated rehearsal teach it. */}
            {isIOSBrowser() && !audioBlob && (
              <p
                className="mt-4 max-w-sm text-center font-sans text-[12px] text-[var(--text-quaternary)]"
                data-testid="ios-recording-note"
              >
                Keep this screen open with the screen on — iPhone and iPad browsers pause the
                microphone if the screen locks or you switch apps.
              </p>
            )}
          </>
        )}

        {/* Uploading */}
        {recStage === "uploading" && (
          <div className="py-8 text-center">
            <Icon name="loader" size={40} className="mx-auto mb-6 animate-spin text-[var(--accent-yellow-text)]" />
            <h2 className="font-serif text-[26px] font-normal text-[var(--text-primary)]">
              {uploadProgress < 100 ? "Uploading…" : "Hans is analyzing your rehearsal…"}
            </h2>
            {uploadProgress < 100 && (
              <div className="mx-auto mt-4 w-48" data-testid="upload-progress">
                <div className="h-1 overflow-hidden rounded-full bg-[var(--studio-interactive)]">
                  {uploadProgress === 0 ? (
                    // `onUploadProgress` is best-effort — WebKit often reports
                    // nothing for a cross-origin PUT — so a hard "0%" claims a
                    // measurement we don't have and reads as a stalled upload.
                    <div className="h-full w-1/3 animate-pulse rounded-full bg-[var(--accent-yellow)]" />
                  ) : (
                    <div className="h-full rounded-full bg-[var(--accent-yellow)] transition-[width] duration-300" style={{ width: `${uploadProgress}%` }} />
                  )}
                </div>
                <p className="mt-2 font-mono text-[13px] text-[var(--text-tertiary)]">
                  {uploadProgress === 0 ? "Preparing upload…" : `${uploadProgress}%`}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Success */}
        {recStage === "success" && (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--accent-yellow)] shadow-[0_0_40px_var(--accent-yellow-glow)]">
              <Icon name="check" size={32} className="text-[var(--text-inverse)]" />
            </div>
            <h2 className="font-serif text-[26px] font-normal text-[var(--text-primary)]">Rehearsal submitted</h2>
            <p className="mt-2 font-sans text-[15px] text-[var(--text-tertiary)]">Your instructor will review your recording.</p>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="flex min-h-[96px] items-center justify-center gap-3 px-4 pb-14 pt-2">
        {recStage === "ready" && (
          <Button size="lg" icon="mic" onPress={onStart} className="min-w-[240px]">Start recording</Button>
        )}
        {recStage === "recording" && !audioBlob && (
          <>
            <Button variant="bare" icon={isPaused ? "play" : "pause"} onPress={isPaused ? onResume : onPause}>
              {isPaused ? "Resume" : "Pause"}
            </Button>
            <Button size="lg" variant="ghost" icon="stop" onPress={onStop} className="min-w-[200px]">Stop</Button>
          </>
        )}
        {recStage === "recording" && audioBlob && (
          <>
            <Button variant="bare" onPress={onDiscard}>Discard</Button>
            <Button size="lg" icon="upload" onPress={onSubmit} className="min-w-[200px]">Submit</Button>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default StudentPracticePage;
